import { Button, ButtonProps } from "@/components/ui/button"
import { useAppKit, useAppKitState } from "@reown/appkit/react"

import { useAuth } from "@/contexts/auth-context"
import { useConnection } from "wagmi"
import { useIntl } from "react-intl"
import { useMemo } from "react"

export function WalletConnectButton({
  className,
  variant = "outline",
  size = "default",
  ...props
}: ButtonProps) {
  const intl = useIntl()
  const { address, isConnected, isConnecting, isReconnecting } = useConnection()
  const { open } = useAppKit()
  const { open: isAppKitOpen } = useAppKitState()
  const {
    connectWallet,
    disconnectWallet,
    isAuthLoading,
    isVerified,
    isFetchingUser,
    isProfileError,
  } = useAuth()

  const isLoading = useMemo(() => {
    if (isReconnecting) {
      return true
    }
    // Show loading when AppKit modal is open
    if (isAppKitOpen) {
      return isConnecting
    }
    // Show loading when manually connecting
    if (isConnecting && isAppKitOpen) {
      return true
    }
    // Don't show loading if profile fetch failed (404 or error)
    // This prevents button from being stuck in "signing..." state
    if (isProfileError) {
      return false
    }
    // Show loading when verifying (connected but not verified yet)
    // But only if we're still fetching user (waiting for profile)
    if (isConnected && !isVerified && isFetchingUser) {
      return true
    }
    // Don't show loading if connected but not verified and not fetching
    // (means we're waiting for user action or error occurred)
    if (isConnected && !isVerified && !isFetchingUser) {
      return false
    }
    return isAuthLoading
  }, [
    isReconnecting,
    isAppKitOpen,
    isConnecting,
    isAuthLoading,
    isConnected,
    isVerified,
    isFetchingUser,
    isProfileError,
  ])

  const buttonText = useMemo(() => {
    if (isReconnecting) {
      return intl.formatMessage({ id: "auth.connecting" })
    }
    if (isConnecting && isAppKitOpen) {
      return intl.formatMessage({ id: "auth.connecting" })
    }
    // Don't show signing if profile fetch failed (user will see modal instead)
    if (isProfileError) {
      return intl.formatMessage({ id: "auth.connectWallet" })
    }
    // Show signing message when connected but not verified and still fetching
    if (isConnected && !isVerified && isFetchingUser) {
      return intl.formatMessage({ id: "auth.signing" })
    }
    // If connected but not verified and not fetching, show connect (error state)
    if (isConnected && !isVerified && !isFetchingUser) {
      return intl.formatMessage({ id: "auth.connectWallet" })
    }
    return intl.formatMessage({ id: "auth.connectWallet" })
  }, [
    isReconnecting,
    isConnecting,
    isConnected,
    isVerified,
    isFetchingUser,
    isProfileError,
    intl,
    isAppKitOpen,
  ])

  const handleConnect = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    connectWallet(() => open({ view: "Connect" }))
  }

  const handleDisconnect = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    await disconnectWallet()
  }

  // Only show address and disconnect when fully verified
  if (isConnected && address && isVerified) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{`${address.slice(
          0,
          6
        )}...${address.slice(-4)}`}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnect}
          disabled={isLoading}
        >
          {isLoading
            ? intl.formatMessage({ id: "auth.disconnecting" })
            : intl.formatMessage({ id: "auth.disconnect" })}
        </Button>
      </div>
    )
  }

  // Show loading button when connected but not verified (signing)
  return (
    <Button
      {...props}
      variant={variant}
      size={size}
      className={className}
      onClick={handleConnect}
      disabled={isLoading || props.disabled}
    >
      {buttonText}
    </Button>
  )
}
